import {
  ChevronLeft,
  ChevronRight,
  Download,
  Maximize2,
  Minimize2,
  Printer,
  RotateCcw,
  RotateCw,
  Scan,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { ReactNode, useState } from "react";
import { downloadOriginalFile, getFileNameFromUrl } from "../../../Utils/fileDownload";

type ViewerToolbarProps = {
  scale?: number;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onFitWidth?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  onRotateLeft?: () => void;
  onRotateRight?: () => void;
  /** Page navigation — pass all four to show it (PDFs only). */
  currentPage?: number;
  totalPages?: number;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  onPrint?: () => void;
  downloadUrl: string;
  fileName?: string;
  nav?: ReactNode;
  isActive?: boolean;
  onDownloadError?: (message: string) => void;
};

export const ViewerToolbar = ({
  scale,
  onZoomIn,
  onZoomOut,
  onFitWidth,
  onToggleFullscreen,
  isFullscreen,
  onRotateLeft,
  onRotateRight,
  currentPage,
  totalPages,
  onPrevPage,
  onNextPage,
  onPrint,
  downloadUrl,
  fileName,
  nav,
  isActive = true,
  onDownloadError,
}: ViewerToolbarProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const hasZoom = !!onZoomIn && !!onZoomOut;
  const hasRotate = !!onRotateLeft && !!onRotateRight;
  const hasPageNav =
    !!onPrevPage && !!onNextPage && !!totalPages && currentPage != null;

  const handleDownload = async () => {
    setIsDownloading(true);
    const name = fileName || getFileNameFromUrl(downloadUrl);
    const result = await downloadOriginalFile(downloadUrl, name);
    if (!result.success) {
      onDownloadError?.("Download failed. Please try again.");
    }
    setIsDownloading(false);
  };

  return (
    <div
      className={`doc-viewer-toolbar ${
        isActive ? "doc-viewer-toolbar--active" : "doc-viewer-toolbar--faded"
      }`}
    >
      <div className="doc-viewer-controls" role="toolbar" aria-label="Viewer">
        {nav}
        {nav ? <span className="doc-viewer-controls__divider" aria-hidden /> : null}

        {hasPageNav && (
          <>
            <button
              type="button"
              className="doc-viewer-icon-btn"
              aria-label="Previous page"
              title="Previous page"
              disabled={currentPage! <= 1}
              onClick={onPrevPage}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="doc-viewer-controls__value" aria-live="polite">
              {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="doc-viewer-icon-btn"
              aria-label="Next page"
              title="Next page"
              disabled={currentPage! >= totalPages!}
              onClick={onNextPage}
            >
              <ChevronRight size={18} />
            </button>
            <span className="doc-viewer-controls__divider" aria-hidden />
          </>
        )}

        {hasZoom && (
          <>
            <button
              type="button"
              className="doc-viewer-icon-btn"
              aria-label="Zoom out"
              title="Zoom out"
              onClick={onZoomOut}
            >
              <ZoomOut size={18} />
            </button>
            <span className="doc-viewer-controls__value" aria-live="polite">
              {Math.round((scale ?? 1) * 100)}%
            </span>
            <button
              type="button"
              className="doc-viewer-icon-btn"
              aria-label="Zoom in"
              title="Zoom in"
              onClick={onZoomIn}
            >
              <ZoomIn size={18} />
            </button>
            <span className="doc-viewer-controls__divider" aria-hidden />

            {onFitWidth && (
              <button
                type="button"
                className="doc-viewer-icon-btn"
                aria-label="Fit to width"
                title="Fit to width"
                onClick={onFitWidth}
              >
                <Scan size={18} />
              </button>
            )}

            {onToggleFullscreen && (
              <button
                type="button"
                className="doc-viewer-icon-btn"
                aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
                title={isFullscreen ? "Exit full screen" : "Full screen"}
                onClick={onToggleFullscreen}
              >
                {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
            )}
            <span className="doc-viewer-controls__divider" aria-hidden />
          </>
        )}

        {hasRotate && (
          <>
            <button
              type="button"
              className="doc-viewer-icon-btn"
              aria-label="Rotate left"
              title="Rotate left"
              onClick={onRotateLeft}
            >
              <RotateCcw size={18} />
            </button>
            <button
              type="button"
              className="doc-viewer-icon-btn"
              aria-label="Rotate right"
              title="Rotate right"
              onClick={onRotateRight}
            >
              <RotateCw size={18} />
            </button>
            <span className="doc-viewer-controls__divider" aria-hidden />
          </>
        )}

        {onPrint && (
          <button
            type="button"
            className="doc-viewer-icon-btn"
            aria-label="Print"
            title="Print"
            onClick={onPrint}
          >
            <Printer size={18} />
          </button>
        )}

        <button
          type="button"
          className="doc-viewer-icon-btn"
          aria-label="Download"
          title="Download original file"
          onClick={handleDownload}
          disabled={isDownloading}
        >
          <Download size={18} />
        </button>
      </div>
    </div>
  );
};